'use client';

import { c as _c } from "react/compiler-runtime";
import { Check } from '@phosphor-icons/react';
import type { CartAddon } from '@/lib/cart/addons';
import { useCurrency } from '@/lib/currency/CurrencyContext';
import { formatPrice } from '@/lib/currency/formatPrice';
import type { AddonGroupDTO } from '@/lib/storefront/products/service';

/**
 * Add-on picker for the PDP.
 *
 * These groups used to render as a read-only list — prices and all — so a
 * customer could see "+ 5.00" beside an extra and had no way to choose it, and
 * nothing they did changed what they paid. Selection is lifted to the PDP so
 * the same value reaches the line total, the cart and the order.
 *
 * A group that admits one choice behaves like a radio (picking replaces);
 * anything else is a checkbox capped at `max_select`.
 */
export function AddonGroupsSelector(t0) {
  const $ = _c(23);
  const {
    groups,
    selected,
    onChange,
    isRTL,
    showErrors
  } = t0;
  const {
    currency
  } = useCurrency();
  let t1;
  if ($[0] !== selected) {
    t1 = function countIn(groupId) {
      return selected.filter(s => s.group_id === groupId).length;
    };
    $[0] = selected;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const countIn = t1;
  let t2;
  if ($[2] !== countIn || $[3] !== onChange || $[4] !== selected) {
    t2 = function toggle(group, item) {
      const isSelected = selected.some(s_0 => s_0.item_id === item.id);
      const single = group.max_select === 1;
      if (isSelected) {
        if (single && group.is_required) {
          return;
        }
        onChange(selected.filter(s_1 => s_1.item_id !== item.id));
        return;
      }
      const addition = {
        group_id: group.id,
        item_id: item.id,
        name_ar: item.name_ar,
        name_en: item.name_en ?? item.name_ar,
        price: item.price
      };
      if (single) {
        onChange([...selected.filter(s_2 => s_2.group_id !== group.id), addition]);
        return;
      }
      const ceiling = group.max_select > 0 ? group.max_select : Number.POSITIVE_INFINITY;
      if (countIn(group.id) >= ceiling) {
        return;
      }
      onChange([...selected, addition]);
    };
    $[2] = countIn;
    $[3] = onChange;
    $[4] = selected;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const toggle = t2;
  let t3;
  if ($[6] !== countIn || $[7] !== currency || $[8] !== groups || $[9] !== isRTL || $[10] !== selected || $[11] !== showErrors || $[12] !== toggle) {
    let t4;
    if ($[14] !== countIn || $[15] !== currency || $[16] !== isRTL || $[17] !== selected || $[18] !== showErrors || $[19] !== toggle) {
      t4 = group_0 => {
        const chosen = countIn(group_0.id);
        const required = group_0.is_required ? Math.max(1, group_0.min_select) : group_0.min_select;
        const unmet = chosen < required;
        const ceiling_0 = group_0.max_select > 0 ? group_0.max_select : Number.POSITIVE_INFINITY;
        return <fieldset key={group_0.id} className="border-none p-0 m-0"><div className="flex items-baseline justify-between mb-2 gap-2"><legend className="text-sm font-medium text-foreground">{isRTL ? group_0.name_ar : group_0.name_en ?? group_0.name_ar}{group_0.is_required && <span className="ms-1 text-danger">*</span>}</legend><p className={`text-xs ${showErrors && unmet ? "text-danger" : "text-muted-foreground"}`}>{group_0.min_select === group_0.max_select ? isRTL ? `اختر ${group_0.min_select}` : `Choose ${group_0.min_select}` : isRTL ? `اختر ${group_0.min_select}-${group_0.max_select}` : `Choose ${group_0.min_select}–${group_0.max_select}`}</p></div><ul className="space-y-1.5">{group_0.items.map(item_0 => {
              const isSelected_0 = selected.some(s_3 => s_3.item_id === item_0.id);
              const atCeiling = !isSelected_0 && chosen >= ceiling_0;
              return <li key={item_0.id}><button type="button" onClick={() => toggle(group_0, item_0)} aria-pressed={isSelected_0} disabled={atCeiling} className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${isSelected_0 ? "border-primary bg-primary/5" : showErrors && unmet ? "border-danger/40 bg-background hover:border-foreground/40" : "border-border bg-background hover:border-foreground/40"} ${atCeiling ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}><span className="flex items-center gap-2.5 min-w-0"><span className={`flex h-4 w-4 shrink-0 items-center justify-center border transition-colors ${group_0.max_select === 1 ? "rounded-full" : "rounded"} ${isSelected_0 ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`} aria-hidden="true">{isSelected_0 && <Check size={10} weight="bold" />}</span><span className="truncate text-foreground">{isRTL ? item_0.name_ar : item_0.name_en ?? item_0.name_ar}</span></span>{item_0.price > 0 && <span className="shrink-0 tabular-nums text-muted-foreground">+ {formatPrice(item_0.price, currency)}</span>}</button></li>;
            })}</ul></fieldset>;
      };
      $[14] = countIn;
      $[15] = currency;
      $[16] = isRTL;
      $[17] = selected;
      $[18] = showErrors;
      $[19] = toggle;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    t3 = groups.map(t4);
    $[6] = countIn;
    $[7] = currency;
    $[8] = groups;
    $[9] = isRTL;
    $[10] = selected;
    $[11] = showErrors;
    $[12] = toggle;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[21] !== t3) {
    t4 = <div className="space-y-4">{t3}</div>;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
}